/**
 * Type definitions for photo markup annotations
 * Feature: 049-critique-photo-markup
 *
 * Markup shapes are stored with percentage-based coordinates (0-100) for
 * resolution-independent rendering across thumbnail/medium/full-size photos.
 */

/**
 * Shape type discriminator
 */
export type ShapeType = 'circle' | 'rectangle' | 'arrow' | 'spotlight';

/**
 * Dash style options for line rendering
 */
export type DashStyle = 'solid' | 'dashed' | 'dotted' | 'hand-drawn';

/**
 * Common shape properties shared by all shape types
 */
interface BaseShape {
  id?: string;                // Optional UUID for client-side state management (not saved to database)
  type: ShapeType;
  number: number;             // 1-9 for @-mentions in comment text
  strokeWidth: number;        // Line thickness in actual pixels (uses vector-effect="non-scaling-stroke")
  strokeColor: string;        // RGBA hex: #RRGGBBAA (e.g., #FF0000FF = opaque red)
  strokeDasharray: string;    // SVG dash pattern: '' | '5,5' | '2,2'
}

/**
 * Circle shape geometry (percentage-based coordinates 0-100)
 */
export interface CircleGeometry {
  cx: number;                 // Center X (0-100 percentage from left)
  cy: number;                 // Center Y (0-100 percentage from top)
  r: number;                  // Radius (0-100 percentage, scaled by min(width, height))
}

/**
 * Rectangle shape geometry (percentage-based coordinates 0-100)
 */
export interface RectangleGeometry {
  x: number;                  // Top-left X (0-100 percentage from left)
  y: number;                  // Top-left Y (0-100 percentage from top)
  width: number;              // Width (0-100 percentage)
  height: number;             // Height (0-100 percentage)
}

/**
 * Arrow shape geometry (percentage-based coordinates 0-100)
 */
export interface ArrowGeometry {
  x1: number;                 // Start X (tail, 0-100 percentage from left)
  y1: number;                 // Start Y (tail, 0-100 percentage from top)
  x2: number;                 // End X (head, 0-100 percentage from left)
  y2: number;                 // End Y (head, 0-100 percentage from top)
}

/**
 * Spotlight shape geometry (percentage-based coordinates 0-100)
 * Same as rectangle, but renders with dimming overlay outside the spotlight area
 */
export interface SpotlightGeometry {
  x: number;                  // Top-left X (0-100 percentage from left)
  y: number;                  // Top-left Y (0-100 percentage from top)
  width: number;              // Width (0-100 percentage)
  height: number;             // Height (0-100 percentage)
}

/**
 * Circle shape (discriminated union member)
 */
export interface CircleShape extends BaseShape {
  type: 'circle';
  geometry: CircleGeometry;
}

/**
 * Rectangle shape (discriminated union member)
 */
export interface RectangleShape extends BaseShape {
  type: 'rectangle';
  geometry: RectangleGeometry;
}

/**
 * Arrow shape (discriminated union member)
 */
export interface ArrowShape extends BaseShape {
  type: 'arrow';
  geometry: ArrowGeometry;
}

/**
 * Spotlight shape (discriminated union member)
 */
export interface SpotlightShape extends BaseShape {
  type: 'spotlight';
  geometry: SpotlightGeometry;
}

/**
 * Discriminated union of all shape types
 */
export type MarkupShape = CircleShape | RectangleShape | ArrowShape | SpotlightShape;

/**
 * Type guards for shape discrimination
 */
export function isCircleShape(shape: MarkupShape): shape is CircleShape {
  return shape.type === 'circle';
}

export function isRectangleShape(shape: MarkupShape): shape is RectangleShape {
  return shape.type === 'rectangle';
}

export function isArrowShape(shape: MarkupShape): shape is ArrowShape {
  return shape.type === 'arrow';
}

export function isSpotlightShape(shape: MarkupShape): shape is SpotlightShape {
  return shape.type === 'spotlight';
}

/**
 * Markup edit data structure (stored in critique_edits.edit_data JSONB)
 */
export interface MarkupEditData {
  shapes: MarkupShape[];      // Array of shapes (max 9)
}

/**
 * Critique edit with markup type
 * (extends existing CritiqueEdit interface pattern)
 */
export interface CritiqueEditWithMarkup {
  id: string;                 // Critique edit global ID (ce prefix)
  critiqueId: string;         // Parent critique ID
  editType: 'markup';
  editData: MarkupEditData;   // Shapes array in JSONB
  baseEditId: string | null;
  createdAt: string;
  updatedAt: string;
}

/**
 * Default style values for new shapes
 */
export const DEFAULT_SHAPE_STYLE = {
  strokeWidth: 3,             // 3px minimum (range: 3-10px)
  strokeColor: '#FF0000FF',   // Opaque red
  strokeDasharray: '',        // Solid line
} as const;

/**
 * Dash style to SVG strokeDasharray mapping
 * Note: 'hand-drawn' uses a special marker that triggers Rough.js rendering
 */
export const DASH_STYLE_MAP: Record<DashStyle, string> = {
  solid: '',
  dashed: '5,5',
  dotted: '2,2',
  'hand-drawn': '__hand_drawn__',
} as const;

/**
 * Validation constants
 */
export const MARKUP_CONSTRAINTS = {
  MAX_SHAPES: 9,
  MIN_NUMBER: 1,
  MAX_NUMBER: 9,
  MIN_CIRCLE_RADIUS: 2,       // Minimum 2% radius
  MIN_RECTANGLE_SIZE: 3,      // Minimum 3% width/height
  MIN_ARROW_LENGTH: 5,        // Minimum 5% distance
} as const;

/**
 * Shape style properties (for style controls)
 */
export interface ShapeStyle {
  strokeWidth: number;
  strokeColor: string;
  strokeDasharray: string;
}
